class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // 26/09/2026 round 55: guard the overlay, it is replaced with the drawer contents on every basket update.
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // 03/10/2026 round 63: a page restored by the Back button keeps the drawer it was first drawn with, so after
    // adding to the basket and going back the drawer showed an empty basket, or showed the items but kept the
    // is-empty flag, which hid the subtotal and the Checkout button. Check against the real basket on those restores.
    window.addEventListener('pageshow', (event) => {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (event.persisted || (nav && nav.type === 'back_forward')) this.syncWithCart();
    });
  }

  syncWithCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        let shown = 0;
        this.querySelectorAll('.cart-item .quantity__input').forEach((input) => {
          shown += parseInt(input.value, 10) || 0;
        });
        if (cart.item_count === shown) {
          this.classList.toggle('is-empty', cart.item_count === 0);
          return;
        }
        return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
          .then((response) => response.json())
          .then((sections) => {
            const drawer = document.getElementById('CartDrawer');
            if (drawer && sections['cart-drawer']) drawer.innerHTML = this.getSectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble && sections['cart-icon-bubble']) bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
            this.classList.toggle('is-empty', cart.item_count === 0);
            this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
          });
      })
      .catch(() => {});
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      // 03/10/2026 round 64: on the basket page the basket is already on screen, so the icon goes to the top of it instead of opening a second basket on top.
      if (document.body.dataset.page === 'cart') return window.scrollTo({ top: 0, behavior: 'smooth' });
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.syncWithCart(); // round 63: never open a drawer that is out of step with the basket
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // 16/09/2026: after an add from an empty basket the element can still carry is-empty while
        // .drawer__inner-empty is already gone, which handed trapFocus a null. Fall back to #CartDrawer.
        const containerToTrapFocusOn =
          (this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : null) ||
          document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.remove('is-empty'); // 16/09/2026: keep the host element in step with its contents
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (document.body.dataset.page !== 'cart') this.open(); // round 64: adds made on the basket page update it in place
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
